import { useState, useRef, useEffect } from 'react';
import { Box, Divider } from '@drivekyte/ui';
import { Illustrations } from '@drivekyte/web-components';
import DesktopNavBarMenu from './desktop-nav-bar-menu';
import Logo from '@/components/nav/components/logo';
import SearchBar from '../desktop-nav-bar-search/search-bar';
import { useNavbarContext } from '../nav-bar-context';
import { MyTripsButton } from './my-trips-button';
import DownloadAppButton from './download-app-button';
import LoginButton from './login-button';
import SignUpButton from './signup-button';
import { MenuButton } from './menu-button';
import { NavbarVariantStateEnum } from '@/constants/navbar';
import { useSearchbarProvider } from '../desktop-nav-bar-search/search-bar-context';

const DesktopNavBar = () => {
  const { isAuthenticated, handlePressSignOut, navState, isDark } =
    useNavbarContext();
  const { isSticky } = useSearchbarProvider();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const isSearchState = navState === NavbarVariantStateEnum.SEARCH;
  const isDefaultState = navState === NavbarVariantStateEnum.DEFAULT;

  const handleMenuClick = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [menuRef]);

  const renderAuthenticatedButtons = () => {
    return (
      <>
        <MyTripsButton />
        <DownloadAppButton />
      </>
    );
  };

  const renderNotAuthenticatedButtons = () => {
    return (
      <>
        <LoginButton />
        <SignUpButton />
      </>
    );
  };

  const renderOpenMenu = () => {
    return (
      <Box
        position="absolute"
        top={56}
        right={isAuthenticated ? 12 : 0}
        ref={menuRef}
      >
        <DesktopNavBarMenu
          isAuthenticated={isAuthenticated}
          handlePressSignOut={handlePressSignOut}
        />
      </Box>
    );
  };

  const renderSearchBar = () => {
    if (isSticky) {
      return (
        <Box
          top={0}
          transition="all 0.5s ease-in-out"
          transform={'translateY(20%)'}
          paddingRight={'$xSmall'}
          style={{ position: 'fixed' }}
          justifyContent="center"
          display="flex"
          zIndex={20}
        >
          <SearchBar />
        </Box>
      );
    }
    return <SearchBar />;
  };

  return (
    <>
      <Box
        width="100%"
        zIndex={10}
        paddingLeft="$large"
        paddingRight="$small"
        paddingVertical="$medium"
        backgroundColor={isDark ? '$green700' : 'primaryB'}
      >
        <Box
          flexDirection="row"
          justifyContent={isSticky ? 'center' : 'space-between'}
          position="relative"
          alignItems="center"
          height="76px"
        >
          <Logo
            name={(isDark ? 'kyte-logo-white' : 'kyte-logo') as Illustrations}
          />
          {(isSearchState || isSticky) && renderSearchBar()}
          <Box flexDirection="row">
            <Box
              flexDirection="row"
              alignItems="center"
              columnGap="$tiny"
              position="relative"
            >
              {isAuthenticated &&
                isDefaultState &&
                renderAuthenticatedButtons()}
              {!isAuthenticated &&
                isDefaultState &&
                renderNotAuthenticatedButtons()}
              <MenuButton handleMenuClick={handleMenuClick} />
              {menuOpen && renderOpenMenu()}
            </Box>
          </Box>
        </Box>
      </Box>
      {isSearchState && <Divider marginVertical="0" />}
    </>
  );
};

export default DesktopNavBar;
